import { useLocale } from '../../../hooks/useLocale';
import { isNil } from 'lodash';
import dayjs from 'dayjs';
import { ref } from '@vue/composition-api';
import {
  StatisticType,
  ContentType,
  IntentCodeType,
  CodeType,
  RiskType,
  ProgressType,
  CustomCommonType,
  ProgressCodeType,
  RiskKeyType,
  GroupData,
  ProcessType,
  SprintProgressCard,
  RiskDataType
} from './type';

import http from 'common-module/src/util/http';

export const useCommonHooks = () => {
  const { t, tc } = useLocale();
  const memberIntentCodeTranslateMap = {
    weekly_report: {
      title: t('saas.robotChat.reportDate'),
      progress: t('saas.robotChat.progressWeek'),
      finishWork: t('saas.robotChat.finishedIssueWeek'),
      inProgressWork: t('saas.robotChat.inProgressWork'),
      overDueWork: t('saas.robotChat.overDueWork'),

      finish: t('saas.robotChat.finish'),
      inProgress: t('saas.robotChat.inProgress'),
      overDue: t('saas.robotChat.overDue')
    },
    daily_report: {
      title: t('saas.robotChat.dailyDate'),
      progress: t('saas.robotChat.progressDay'),
      finishWork: t('saas.robotChat.finishedIssueDay'),
      inProgressWork: t('saas.robotChat.inProgressWorkDay'),
      overDueWork: t('saas.robotChat.overDueWork'),

      finish: t('saas.robotChat.finish'),
      inProgress: t('saas.robotChat.inProgress'),
      overDue: t('saas.robotChat.overDue')
    }
  };
  const intentCodeTranslateMap = {
    weekly_report: {
      title: t('saas.robotChat.reportDate'),
      progress: t('saas.robotChat.progressWeek'),
      finishWork: t('saas.robotChat.finishWorkWeek'),
      inProgressWork: t('saas.robotChat.inProgressWork'),
      overDueWork: t('saas.robotChat.overDueWork'),

      finish: t('saas.robotChat.finish'),
      inProgress: t('saas.robotChat.inProgress'),
      overDue: t('saas.robotChat.overDue')
    },
    daily_report: {
      title: t('saas.robotChat.dailyDate'),
      progress: t('saas.robotChat.progressDay'),
      finishWork: t('saas.robotChat.finishWorkDay'),
      inProgressWork: t('saas.robotChat.inProgressWorkDay'),
      overDueWork: t('saas.robotChat.overDueWorkDay'),

      finish: t('saas.robotChat.finish'),
      inProgress: t('saas.robotChat.inProgress'),
      overDue: t('saas.robotChat.overDue')
    },
    risk: {
      title: t('saas.robotChat.iterationCycle'),
      total: t('saas.robotChat.totalIssue'),
      inProgress: t('saas.robotChat.inProgress'),
      done: t('saas.robotChat.finish'),
      riskSituation: t('saas.robotChat.riskSituation'),
      overDueWorkRisk: t('saas.robotChat.overDueRisk'),
      memberRisk: t('saas.robotChat.memberRiskWork'),
      progressRisk: t('saas.robotChat.progressRisk'),
      taskRisk: t('saas.robotChat.taskRisk'),
      currentRate: t('saas.robotChat.currentRate')
    }
  };
  const processTranslateMap = {
    title: t('saas.robotChat.sprintProgress'),
    total: t('saas.robotChat.totalIssue'),
    inProgress: t('saas.robotChat.inProgress'),
    done: t('saas.robotChat.finish'),
    riskSituation: t('saas.robotChat.riskSituation'),
    overDueWorkRisk: t('saas.robotChat.overDueRisk'),
    memberRisk: t('saas.robotChat.memberRiskWork'),
    progressRisk: t('saas.robotChat.progressRisk'),
    taskRisk: t('saas.robotChat.taskRisk'),
    currentRate: t('saas.robotChat.currentRate'),
    finishedIssueIds: t('saas.robotChat.finishedIssueDay'),
    unfinishedIssueIds: t('saas.robotChat.inProgressWork'),
    point: t('saas.robotChat.progressPointKey'),
    labour: t('saas.robotChat.labourHours'),
    issueCount: t('saas.robotChat.progressWorkCount')
  };

  const unitTranslateMap = {
    point: (num: number) => (`${num} ${tc('home.chartWidget.point', num)}`),
    labour: () => 'h',
    issueCount: (num: number) => tc('common.count.issue', num)
  };

  const levelUnit = {
    3: t('saas.robotChat.High'),
    2: t('saas.robotChat.Medium'),
    1: t('saas.robotChat.Low'),
    0: t('saas.robotChat.None')
  };

  const transformUnitByStatisticType = (type: StatisticType, value: ProcessType) => {
    if (type === 'point') {
      return unitTranslateMap.point(value.point || 0);
    }
    if (type === 'labour') {
      return `${value.labour || 0} ${unitTranslateMap.labour()}`;
    }
    if (type === 'issueCount') {
      return `${value.issueCount || 0} ${unitTranslateMap.issueCount(value.issueCount)}`;
    }
  };


  const transformIssueText = ({
    intentCode,
    workStatisticType,
    issue,
    fixedPoint = false,
    fixedLabour = false,
    fixedIssueCount = false,
    fixedProgress = true
  }: {
    intentCode: IntentCodeType,
    issue: ContentType,
    workStatisticType?: StatisticType,
    fixedPoint?: boolean,
    fixedLabour?: boolean,
    fixedIssueCount?: boolean,
    fixedProgress?: boolean,
  }): string => {
    const s = issue.summary;
    const point = unitTranslateMap.point(issue.point);
    const labour = issue.labour + unitTranslateMap.labour();
    const issueCount = issue.issueCount + ' ' + unitTranslateMap.issueCount(issue.issueCount);
    let p = '', l = '', i = '';
    if (issue.type === 'bug') {
      i = issueCount;
    } else if (!workStatisticType) {
      p = issue.point > 0 ? point : '';
      l = issue.labour > 0 ? labour : '';
      i = issue.issueCount > 0 ? issueCount : '';
    } else {
      p = workStatisticType === 'point' ? point
        : fixedPoint ? point : '';
      l = workStatisticType === 'labour' ? labour
        : fixedLabour ? labour : '';
      i = workStatisticType === 'issueCount' ? issueCount
        : fixedIssueCount ? issueCount : '';
    }

    const c = fixedProgress ? t('saas.robotChat.overallProgress', { process: issue.process }) : '';
    const w = (p || l || i || c) ?
      (intentCode === 'weekly_report' ? t('saas.robotChat.lastWeekTitle') : t('saas.robotChat.lastDayTitle'))
      : '';
    return `${s} ${w} ${p} ${l} ${i} ${c}`;
  };

  const transformListNeedData = (
    key: CodeType,
    listMap: Array<CustomCommonType>
  ): Array<GroupData> => {
    if (key !== 'finishWork') {
      return [];
    }
    const find = listMap.find((item) => item.key === 'finishWork') || { content: [] };
    return find.content?.length > 0 ? find.content.map((item: ContentType) => {
      return {
        title: item.summary,
        issueIds: item.issueIds
      };
    }) : [];
  };

  const transformSummaryNeedData = (
    { intentCode, summaryList }: { intentCode: IntentCodeType, summaryList: ProgressType }
  ): Array<GroupData> => {

    const keys: Array<ProgressCodeType> = ['finish', 'inProgress', 'overDue'];
    return keys.map((key) => {
      return {
        title: intentCodeTranslateMap[intentCode][key],
        issueIds: summaryList[key]?.issueIds
      };
    });
  };

  const transformMemberListNeedData = (key: RiskKeyType, listMap: Array<RiskType>): Array<GroupData> | undefined | null => {
    if (key !== 'memberRisk') {
      return null;
    }
    const find = listMap.find((item: RiskType) => item.key === 'memberRisk');

    return find ? find?.users?.map(user => ({
      ...user,
      title: user.name,
      issueIds: user.issueIds
    })) : null;
  };

  return {
    memberIntentCodeTranslateMap,
    intentCodeTranslateMap,
    processTranslateMap,
    unitTranslateMap,
    levelUnit,
    transformUnitByStatisticType,
    transformIssueText,
    transformListNeedData,
    transformSummaryNeedData,
    transformMemberListNeedData
  };
};

type TimeObject = { isToday: boolean, days: number, isOverdue: boolean};
export const formatRemindTime = ({ isToday, days, isOverdue }: TimeObject, t: any, tc: any) => {
  if (isToday) {
    return t('kanbans.today_end');
  }
  if (isOverdue) {
    return tc('kanbans.overdue', days, { day: days });
  }
  return tc('kanbans.days_remaining', days, { day: days });
};

export const calcRemainingDays = async (data: SprintProgressCard | RiskDataType): Promise<TimeObject> => {
  if (isNil(data.startTime) || isNil(data.endTime)) {
    return {
      isToday: false,
      days: 0,
      isOverdue: false
    };
  }
  const time = dayjs(data.endTime).startOf('day');
  const now = dayjs().startOf('day');
  return {
    isToday: time.isToday(),
    days: now.isAfter(time) ? data.overdueDays : data.remainingDays,
    isOverdue: now.isAfter(time)
  };
};

